import * as React from "react";
import { createPortal } from "react-dom";
import { Icon, IconInput } from "../components/Icon";

/**
 * Transient notifications.
 *
 * This deliberately does NOT port ScadsysIdentity's general/alertStore.js. That module is a pub/sub
 * with a `subscribe()` that nothing in the app ever calls, so the ~15 files importing `alert(...)`
 * push messages into a void and the user is never told anything happened. This is a real provider:
 * `useToast()` returns the push functions and a mounted `<Toaster/>` renders them.
 *
 * Use it for the outcome of an action the user took (saved, deleted, calculation run). Errors that
 * belong to a piece of the page - a failed list load, a field validation - stay inline as a
 * `<Banner>` where the user is looking, rather than being thrown into the corner.
 */

export type ToastTone = "info" | "success" | "warning" | "danger";

export interface Toast {
    id: number;
    tone: ToastTone;
    message: React.ReactNode;
    /** ms before auto-dismiss; errors stay until dismissed. */
    duration: number;
}

interface ToastApi {
    push: (tone: ToastTone, message: React.ReactNode, duration?: number) => void;
    success: (message: React.ReactNode) => void;
    info: (message: React.ReactNode) => void;
    warning: (message: React.ReactNode) => void;
    error: (message: React.ReactNode) => void;
    dismiss: (id: number) => void;
}

const ToastContext = React.createContext<ToastApi | null>(null);

const TONES: Record<ToastTone, { classes: string; icon: IconInput }> = {
    info: { classes: "bg-info-soft border-info-border text-info-text", icon: ["fad", "circle-info"] },
    success: { classes: "bg-success-soft border-success-border text-success-text", icon: ["fad", "circle-check"] },
    warning: { classes: "bg-warning-soft border-warning-border text-warning-text", icon: ["fad", "triangle-exclamation"] },
    danger: { classes: "bg-danger-soft border-danger-border text-danger-text", icon: ["fad", "circle-exclamation"] },
};

let nextId = 1;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = React.useState<Toast[]>([]);
    const timers = React.useRef<Record<number, ReturnType<typeof setTimeout>>>({});

    const dismiss = React.useCallback((id: number) => {
        setToasts((current) => current.filter((t) => t.id !== id));
        const timer = timers.current[id];
        if (timer) {
            clearTimeout(timer);
            delete timers.current[id];
        }
    }, []);

    const push = React.useCallback(
        (tone: ToastTone, message: React.ReactNode, duration?: number) => {
            const id = nextId++;
            // An error the user has to act on should not disappear while they are reading it.
            const ttl = duration ?? (tone === "danger" ? 0 : 5000);
            setToasts((current) => [...current, { id, tone, message, duration: ttl }]);
            if (ttl > 0) {
                timers.current[id] = setTimeout(() => dismiss(id), ttl);
            }
        },
        [dismiss]
    );

    // Clear any pending timers if the provider itself goes away.
    React.useEffect(
        () => () => {
            Object.values(timers.current).forEach(clearTimeout);
            timers.current = {};
        },
        []
    );

    const api = React.useMemo<ToastApi>(
        () => ({
            push,
            dismiss,
            success: (m) => push("success", m),
            info: (m) => push("info", m),
            warning: (m) => push("warning", m),
            error: (m) => push("danger", m),
        }),
        [push, dismiss]
    );

    return (
        <ToastContext.Provider value={api}>
            {children}
            <Toaster toasts={toasts} onDismiss={dismiss} />
        </ToastContext.Provider>
    );
};

const Toaster: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => {
    if (typeof document === "undefined" || !toasts.length) return null;

    return createPortal(
        <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2" aria-live="polite">
            {toasts.map((toast) => {
                const { classes, icon } = TONES[toast.tone];
                return (
                    <div
                        key={toast.id}
                        role={toast.tone === "danger" ? "alert" : "status"}
                        className={`pointer-events-auto flex items-start gap-2 rounded-md border p-3 text-sm shadow-lg ${classes}`}
                    >
                        <Icon icon={icon} className="mt-0.5" />
                        <div className="min-w-0 flex-1 break-words">{toast.message}</div>
                        <button
                            type="button"
                            onClick={() => onDismiss(toast.id)}
                            className="rounded p-0.5 opacity-70 transition-opacity hover:opacity-100"
                        >
                            <span className="sr-only">Dismiss</span>
                            <Icon icon={["fad", "xmark"]} />
                        </button>
                    </div>
                );
            })}
        </div>,
        document.body
    );
};

export function useToast(): ToastApi {
    const api = React.useContext(ToastContext);
    if (!api) {
        throw new Error("useToast must be used inside a <ToastProvider>. It is mounted once, in App.tsx.");
    }
    return api;
}
